// SearchSuggestionModal.js - Global Search & Suggestion Modal
// Accessible from Desktop navbar search bar and Mobile navbar search icon (also Cmd+K shortcut).
// Displays live search, Top 10 suggestion chips, matching store templates, and highlighted custom store button.

import React, { useState, useEffect, useRef } from 'react';
import { allTemplates, demoStoreUrl, templatesForVisitor } from './Hero';

const heroTemplates = templatesForVisitor(allTemplates); // the international stores too, first outside India
import { TOP_10_SEARCH_SUGGESTIONS } from '../utils/categoriesData';
import { openCustomWebsiteModal } from '../utils/customModal';
import { trackGetFreeClick } from '../utils/track';
import './SearchSuggestionModal.css';

export default function SearchSuggestionModal({ isOpen, onClose, initialQuery = '' }) {
  const [query, setQuery] = useState(initialQuery || '');
  const inputRef = useRef(null);

  // Auto-focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery(initialQuery || '');
      setTimeout(() => {
        inputRef.current?.focus();
      }, 60);
    }
  }, [isOpen, initialQuery]);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const cleanQ = query.toLowerCase().trim();

  // Filter templates
  const matchingTemplates = heroTemplates.filter((t) => {
    if (!cleanQ) return false;
    const haystack = `${t.name} ${t.label} ${t.note} ${t.business} ${t.pitch}`.toLowerCase();
    return haystack.includes(cleanQ);
  });

  const handleSelectSuggestion = (suggestion) => {
    onClose();
    window.location.href = `/websites?q=${encodeURIComponent(suggestion.label)}`;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onClose();
      if (cleanQ) {
        window.location.href = `/websites?q=${encodeURIComponent(cleanQ)}`;
      } else {
        window.location.href = '/websites';
      }
    }
  };

  const handleCreateCustom = () => {
    onClose();
    openCustomWebsiteModal(cleanQ);
  };

  return (
    <div
      className="search-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="search-modal-input"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="search-modal-container">
        {/* Search Header Bar */}
        <div className="search-modal-input-bar">
          <span className="search-modal-icon" aria-hidden="true">
            🔍
          </span>
          <input
            ref={inputRef}
            id="search-modal-input"
            type="text"
            className="search-modal-field"
            placeholder="Search 100+ website templates (e.g. clothing, phone cases, plants, gifts...)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Search website templates"
          />
          {query && (
            <button
              type="button"
              className="search-modal-clear"
              onClick={() => {
                setQuery('');
                inputRef.current?.focus();
              }}
              aria-label="Clear search input"
            >
              ✕
            </button>
          )}
          <button type="button" className="search-modal-close" onClick={onClose} aria-label="Close search" title="Close (ESC)">
            ✕
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="search-modal-body">
          {/* Highlighted 'Create Your Own Custom Website' Action Card */}
          <div className="search-modal-highlighted-card">
            <div className="card-left">
              <span className="sparkle-pulse">✨</span>
              <div className="card-texts">
                <strong>Create Your Own Custom Website</strong>
                <span>If your business is not in the list, build your custom store in 60s</span>
              </div>
            </div>
            <button
              type="button"
              className="btn-modal-custom-cta"
              onClick={handleCreateCustom}
            >
              {cleanQ ? `Build for "${query}" →` : 'Create Custom Store →'}
            </button>
          </div>

          {/* Top 10 Suggestion Chips */}
          <div className="search-modal-suggestions-section">
            <span className="suggestions-title">🔥 Top Suggestions:</span>
            <div className="suggestions-chips-grid" role="group" aria-label="Top suggested searches">
              {TOP_10_SEARCH_SUGGESTIONS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="suggestion-chip-btn"
                  onClick={() => handleSelectSuggestion(item)}
                >
                  <span className="chip-emoji">{item.icon}</span>
                  <span className="chip-name">{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Live Search Matching Results */}
          {cleanQ && (
            <div className="search-modal-results-section">
              <div className="results-section-header">
                <span>Matching Stores ({matchingTemplates.length})</span>
                <a
                  href={`/websites?q=${encodeURIComponent(cleanQ)}`}
                  className="results-view-all"
                  onClick={onClose}
                >
                  View all in catalog →
                </a>
              </div>

              {matchingTemplates.length > 0 ? (
                <div className="results-items-list">
                  {matchingTemplates.map((t) => {
                    const demoUrl = demoStoreUrl(t);
                    return (
                      <div key={t.id} className="search-result-row">
                        <div className="row-left">
                          <span className="row-icon">{t.icon}</span>
                          <div className="row-info">
                            <div className="row-title-line">
                              <strong>{t.name}</strong>
                              <span className="row-badge">{t.label}</span>
                            </div>
                            <span className="row-pitch">{t.pitch}</span>
                          </div>
                        </div>

                        <div className="row-actions">
                          {demoUrl && (
                            <a
                              href={demoUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="btn-row-demo"
                              onClick={() => {
                                trackGetFreeClick();
                                onClose();
                              }}
                            >
                              👀 Live Demo
                            </a>
                          )}
                          <a
                            href={demoUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-row-get"
                            onClick={() => {
                              trackGetFreeClick();
                              onClose();
                            }}
                          >
                            🚀 Get Website
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="results-none-box">
                  <span className="none-icon">🔎</span>
                  <p>
                    No pre-built template found for <strong>&quot;{query}&quot;</strong>.
                  </p>
                  <button
                    type="button"
                    className="btn-none-build-custom"
                    onClick={handleCreateCustom}
                  >
                    ✨ Build Custom Website for &quot;{query}&quot;
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="search-modal-footer">
          <a
            href="/websites"
            className="modal-footer-catalog-link"
            onClick={onClose}
          >
            <span>Browse all 100+ ready-to-use business websites with filters</span>
            <span aria-hidden="true">→</span>
          </a>
          <span className="modal-footer-kbd-hint">
            Press <kbd>↵ Enter</kbd> to search or <kbd>ESC</kbd> to close
          </span>
        </div>
      </div>
    </div>
  );
}
